/**
 * EditSessionDialog (PR-S1, May 2026).
 *
 * Used from SessionDetail.tsx to edit an existing session. Same form
 * pattern as @/components/teams/AddEditTeamDialog (PR-X1) — react-hook-form
 * + Zod resolver + isPendingLike pending state + toast feedback.
 *
 * Constraints (per PR-S1 lock-in):
 *   - location: enum Select (Strongsville | Solon)
 *   - ageGroup: enum Select ("5-8 years" | "8+ years") — matches the
 *     two canonical values used by the existing /api/coach/sessions list
 *     handler. Schema column itself is TEXT (loose), but the form drops a
 *     dropdown to keep new edits clean.
 *   - NO team/batch picker — sessions don't have a batchId column
 *   - NO coach picker — coach reassignment intentionally not in PR-S1
 *   - NO sessionType change beyond keeping current value (defaults shown)
 *
 * Toast: @/hooks/use-toast (real Toaster). Never @/shared/toast.
 */

import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Save, X } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

const LOCATIONS = ["Strongsville", "Solon"] as const;
const AGE_GROUPS = ["5-8 years", "8+ years"] as const;

const formSchema = z.object({
  title: z.string().min(1, "Title is required").max(255),
  description: z.string().optional().nullable(),
  ageGroup: z.enum(AGE_GROUPS, {
    errorMap: () => ({ message: "Pick an age group" }),
  }),
  location: z.enum(LOCATIONS, {
    errorMap: () => ({ message: "Pick a venue" }),
  }),
  // <input type="datetime-local"> emits "YYYY-MM-DDTHH:MM" strings — no
  // timezone. We treat them as local time and convert to ISO before send.
  startLocal: z.string().min(1, "Start time is required"),
  endLocal: z.string().min(1, "End time is required"),
  maxPlayers: z.number().int().positive().optional().nullable(),
});

type FormValues = z.infer<typeof formSchema>;

interface SessionForEdit {
  id: number;
  title: string;
  description: string | null;
  sessionType: string;
  ageGroup: string;
  location: string;
  startTime: string; // ISO
  endTime: string;   // ISO
  maxPlayers: number | null;
}

interface Props {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  session: SessionForEdit;
}

/**
 * Convert ISO timestamp → "YYYY-MM-DDTHH:MM" (local) for
 * <input type="datetime-local">. Browsers interpret this exact format
 * as local time; we round-trip via Date so we honor the user's TZ.
 */
function isoToLocalInput(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  // Subtract the TZ offset so toISOString().slice(0,16) reflects local clock.
  const tzOffsetMs = d.getTimezoneOffset() * 60_000;
  return new Date(d.getTime() - tzOffsetMs).toISOString().slice(0, 16);
}

/** Reverse: "YYYY-MM-DDTHH:MM" (local) → ISO UTC string. */
function localInputToIso(local: string): string {
  if (!local) return "";
  // new Date("YYYY-MM-DDTHH:MM") parses as LOCAL time in browsers.
  const d = new Date(local);
  return isNaN(d.getTime()) ? "" : d.toISOString();
}

/** Coerce a DB value into one of the canonical age-group enum values, or
 * fall back to the first option if the legacy value isn't recognized.
 * The Zod schema rejects non-enum values; the dropdown can't display
 * arbitrary text either. This keeps Edit usable on legacy rows whose
 * ageGroup is e.g. "Foundation (5-8)" — the user just re-selects. */
function normalizeAgeGroup(raw: string): (typeof AGE_GROUPS)[number] {
  return (AGE_GROUPS as readonly string[]).includes(raw)
    ? (raw as (typeof AGE_GROUPS)[number])
    : "5-8 years";
}
function normalizeLocation(raw: string): (typeof LOCATIONS)[number] {
  return (LOCATIONS as readonly string[]).includes(raw)
    ? (raw as (typeof LOCATIONS)[number])
    : "Strongsville";
}

export function EditSessionDialog({ open, onOpenChange, session }: Props) {
  const { toast } = useToast();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: session.title,
      description: session.description ?? "",
      ageGroup: normalizeAgeGroup(session.ageGroup),
      location: normalizeLocation(session.location),
      startLocal: isoToLocalInput(session.startTime),
      endLocal: isoToLocalInput(session.endTime),
      maxPlayers: session.maxPlayers ?? undefined,
    },
  });

  // Re-populate when the dialog opens with a different session (e.g.,
  // user navigates between sessions without unmounting the dialog tree).
  useEffect(() => {
    if (open) {
      form.reset({
        title: session.title,
        description: session.description ?? "",
        ageGroup: normalizeAgeGroup(session.ageGroup),
        location: normalizeLocation(session.location),
        startLocal: isoToLocalInput(session.startTime),
        endLocal: isoToLocalInput(session.endTime),
        maxPlayers: session.maxPlayers ?? undefined,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, session.id]);

  const saveMutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const payload = {
        title: values.title,
        description: values.description || null,
        ageGroup: values.ageGroup,
        location: values.location,
        startTime: localInputToIso(values.startLocal),
        endTime: localInputToIso(values.endLocal),
        maxPlayers: values.maxPlayers ?? null,
      };
      return apiRequest("PATCH", `/api/coach/sessions/${session.id}`, payload);
    },
    onSuccess: () => {
      toast({
        title: "Session updated",
        description: "Changes saved.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/coach/sessions"] });
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to update session",
        description: error.message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  function onSubmit(values: FormValues) {
    // Pre-flight: ensure end is after start (server enforces too — this is
    // for an immediate inline error on the field instead of a 400 toast).
    if (
      values.startLocal &&
      values.endLocal &&
      new Date(values.endLocal) <= new Date(values.startLocal)
    ) {
      form.setError("endLocal", {
        type: "manual",
        message: "End must be after start",
      });
      return;
    }
    saveMutation.mutate(values);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="edit-session-modal"
      >
        <DialogHeader>
          <DialogTitle>Edit session</DialogTitle>
          <DialogDescription>
            Update the session details. Cancel and reschedule from the detail
            page if you need to take it out of the upcoming list entirely.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Title */}
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem className="md:col-span-2">
                    <FormLabel>Title *</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g. U12 Wednesday Practice"
                        data-testid="input-session-title"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Age group */}
              <FormField
                control={form.control}
                name="ageGroup"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Age group *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger data-testid="select-session-age-group">
                          <SelectValue placeholder="Pick an age group" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {AGE_GROUPS.map((g) => (
                          <SelectItem key={g} value={g}>
                            {g}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Location */}
              <FormField
                control={form.control}
                name="location"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Venue *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger data-testid="select-session-location">
                          <SelectValue placeholder="Pick a venue" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {LOCATIONS.map((l) => (
                          <SelectItem key={l} value={l}>
                            {l}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Start time */}
              <FormField
                control={form.control}
                name="startLocal"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Start *</FormLabel>
                    <FormControl>
                      <Input
                        type="datetime-local"
                        data-testid="input-session-start"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* End time */}
              <FormField
                control={form.control}
                name="endLocal"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>End *</FormLabel>
                    <FormControl>
                      <Input
                        type="datetime-local"
                        data-testid="input-session-end"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Max players */}
              <FormField
                control={form.control}
                name="maxPlayers"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Max players</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={1}
                        max={200}
                        placeholder="20"
                        data-testid="input-session-max-players"
                        {...field}
                        value={field.value ?? ""}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value === ""
                              ? null
                              : Number(e.target.value),
                          )
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Description */}
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem className="md:col-span-2">
                    <FormLabel>Notes</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Bring water bottles. Indoor cricket."
                        data-testid="input-session-description"
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                data-testid="btn-cancel-session-edit"
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saveMutation.isPending}
                data-testid="btn-save-session"
              >
                {saveMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving…
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save changes
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
